/* eslint-disable no-unused-vars */
// @ts-nocheck
import React, { useCallback, useState } from 'react';
import { Button, Stack, styled } from '@mui/material';
import { ReactComponent as AddPhotoAlternate } from '../../assets/Icons/lucideIcons/image-plus.svg';
import { ReactComponent as FormatAlignCenter } from '../../assets/Icons/lucideIcons/text-align-center.svg';
import { ReactComponent as FormatAlignJustify } from '../../assets/Icons/lucideIcons/text-align-justify.svg';
import { ReactComponent as FormatAlignLeft } from '../../assets/Icons/lucideIcons/text-align-start.svg';
import { ReactComponent as FormatAlignRight } from '../../assets/Icons/lucideIcons/text-align-end.svg';
import { ReactComponent as InsertLink } from '../../assets/Icons/lucideIcons/link-2.svg';
import { ReactComponent as LinkOff } from '../../assets/Icons/lucideIcons/link-2-off.svg';
import { ReactComponent as Redo } from '../../assets/Icons/lucideIcons/rotate-cw.svg';
import { ReactComponent as TableChart } from '../../assets/Icons/lucideIcons/table.svg';
import { ReactComponent as Undo } from '../../assets/Icons/lucideIcons/undo.svg';
import { ReactComponent as WebAsset } from '../../assets/Icons/lucideIcons/layout-grid.svg';
import { ReactComponent as YouTube } from '../../assets/Icons/lucideIcons/circle-play.svg';
import { ReactComponent as ArrowDropDownIcon } from '../../assets/Icons/lucideIcons/chevron-down.svg';
import { Editor } from '@tiptap/react';
import PropTypes from 'prop-types';

import useSolyticsEditorToolbar, {
  TEXT_STYLE_OPTIONS,
  FONT_SIZES,
} from './useSolyticsEditorToolbar';
import toolbarItems from './toolbarItems';
import ImageUploadModal from './modals/UploadImageModal';
import {
  ToggleButton,
  ToolbarBaseButton,
  ToolbarDropdown,
  ToolbarMenuItem,
  ToolbarVerticalDivider,
} from './components';
import TableBubbleMenu from './menus/TableBubbleMenu';
import ResizableImageBubbleMenu from './menus/ResizableImageBubbleMenu';
import IFrameModal from './modals/IFrameModal';
import LinkModal from './modals/LinkModal';
import { useSolyticsEditorContext } from './context/SolyticsEditorContext';

const SolyticsEditorToolbarRoot = styled(Stack, {
  name: 'SolyticsEditorToolbar',
  slot: 'root',
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  backgroundColor: theme?.palette?.background?.default,
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  flexWrap: 'wrap',
  border: `1px solid ${theme?.palette?.other?.editorBorder}`,
  borderRadius: 8,
  position: 'sticky',
  top: '80px',
  // zIndex: theme.zIndex.fab,
  // boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
}));

/**
 * @typedef SolyticsEditorToolbarProps
 * @property {import('@tiptap/react').Editor} editor
 */

/**
 * Editor toolbar component
 * @type {import('react').FC<SolyticsEditorToolbarProps & import('@mui/material').StackProps>}
 */
const SolyticsEditorToolbar = ({ editor, ...props }) => {
  const { hiddenToolbarItems } = useSolyticsEditorContext();
  const {
    handleToggle,
    canRedo,
    canUndo,
    undo,
    redo,
    getActiveTextStyle,
    getCurrentFont,
    addImage,
    insertTable,
    handleTextAlign,
  } = useSolyticsEditorToolbar(editor);
  const [uploadImageModalOpen, setUploadImageModalOpen] = useState(false);
  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [youtubeModalOpen, setYoutubeModalOpen] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [currentLink, setCurrentLink] = useState('');

  const handleTextStyleChange = useCallback(
    (e) => {
      const textStyle = e?.target?.value;
      if (textStyle === 0) {
        handleToggle('paragraph');
      } else {
        handleToggle('heading', {
          level: textStyle,
        });
      }
    },
    [handleToggle]
  );

  const handleToggleUploadImageModal = useCallback(
    () => setUploadImageModalOpen((prev) => !prev),
    [setUploadImageModalOpen]
  );

  const handleInsertTable = useCallback(
    () => insertTable({ cols: 2, rows: 1, withHeaderRow: false }),
    [insertTable]
  );

  const getTextAlignHandler = useCallback(
    (alignment) => {
      return () => handleTextAlign(alignment);
    },
    [handleTextAlign]
  );

  const handleInsertYoutubeVideo = useCallback(
    (url) => {
      if (url) {
        editor.commands.setYoutubeVideo({
          src: url,
        });
      }
    },
    [editor]
  );

  const handleInsertIframe = useCallback(
    (url) => {
      if (url) {
        editor.chain().focus().setIframe({ src: url }).run();
      }
    },
    [editor]
  );

  const handleInsertSetLink = useCallback(
    (url) => {
      if (!url) {
        editor.chain().focus().unsetLink().run();
      }
      editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    },
    [editor]
  );

  const handleToggleYoutubeModal = useCallback(
    () => setYoutubeModalOpen((prev) => !prev),
    [setYoutubeModalOpen]
  );
  const handleToggleIFrameModal = useCallback(
    () => setIframeModalOpen((prev) => !prev),
    [setIframeModalOpen]
  );

  const handleToggleLinkModal = useCallback(() => {
    const url = editor.getAttributes('link')?.href;
    setCurrentLink(url);
    setLinkModalOpen((prev) => !prev);
  }, [setLinkModalOpen, editor, setCurrentLink, setLinkModalOpen]);

  const handleInsertAdvanceTableFiled = useCallback(() => {
    const uniqueId = new Date().getTime(); // Generate unique timestamp
    editor
      ?.chain()
      ?.focus()
      ?.setAdvanceTableWithFields({
        id: uniqueId.toString(),
        name: `table-${uniqueId}`,
      })
      ?.run();
  }, [editor]);

  const handleAddChart = useCallback(() => {
    const uniqueId = new Date().getTime(); // Generate unique timestamp
    editor
      ?.chain()
      ?.focus()
      ?.setChartWithFields({
        id: uniqueId.toString(),
        name: `chart-${uniqueId}`,
      })
      ?.run();
  }, [editor]);

  const handleSetFontSize = useCallback(
    (e) => {
      editor?.chain()?.focus()?.setFontSize(e?.target?.value)?.run();
    },
    [editor]
  );

  const renderToggleButton = (item) => {
    return (
      <ToggleButton
        key={item.action}
        action={item.action}
        editor={editor}
        handleToggle={handleToggle}
        title={item?.title}
        aria-label={item?.title}
      >
        {item.buttonContent}
      </ToggleButton>
    );
  };

  const renderDivider = (_item) => {
    return (
      <div style={{ display: 'flex', padding: '4px' }}>
        <ToolbarVerticalDivider />
      </div>
    );
  };

  const renderUndoButton = (item) => {
    return (
      <ToolbarBaseButton
        onClick={undo}
        disabled={!canUndo}
        aria-label={item?.title}
        title={item?.title}
      >
        <Undo width={16} height={16} />
      </ToolbarBaseButton>
    );
  };

  const renderRedoButton = (item) => {
    return (
      <ToolbarBaseButton
        onClick={redo}
        disabled={!canRedo}
        aria-label={item?.title}
        title={item?.title}
      >
        <Redo width={16} height={16} />
      </ToolbarBaseButton>
    );
  };

  const renderHeadingDropdown = (_item) => {
    return (
      <ToolbarDropdown
        value={getActiveTextStyle()}
        onChange={handleTextStyleChange}
        select
        fullWidth={false}
        sx={{
          minWidth: 160,
        }}
        slotProps={{
          select: {
            IconComponent: ArrowDropDownIcon,
            slotProps: {
              menu: {
                slotProps: {
                  paper: {
                    sx: {
                      boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
                    },
                  },
                },
              },
            },
          },
        }}
      >
        {TEXT_STYLE_OPTIONS.map((textStyle) => (
          <ToolbarMenuItem key={textStyle?.value} value={textStyle?.value}>
            {textStyle?.label}
          </ToolbarMenuItem>
        ))}
      </ToolbarDropdown>
    );
  };

  const renderImageButton = (_item) => {
    return (
      <ToolbarBaseButton onClick={handleToggleUploadImageModal}>
        <AddPhotoAlternate width={18} height={18} />
      </ToolbarBaseButton>
    );
  };

  const renderTableButton = (_item) => {
    return (
      <ToolbarBaseButton onClick={handleInsertTable}>
        <TableChart width={18} height={18} />
      </ToolbarBaseButton>
    );
  };

  const renderTextAlignControls = (item) => {
    return (
      <>
        <ToggleButton
          editor={editor}
          action={{ textAlign: 'left' }}
          onClick={getTextAlignHandler('left')}
          title={item?.title?.left}
          aria-label={item?.title?.left}
        >
          <FormatAlignLeft width={18} height={18} />
        </ToggleButton>
        <ToggleButton
          editor={editor}
          action={{ textAlign: 'center' }}
          onClick={getTextAlignHandler('center')}
          title={item?.title?.center}
          aria-label={item?.title?.center}
        >
          <FormatAlignCenter width={18} height={18} />
        </ToggleButton>
        <ToggleButton
          editor={editor}
          action={{ textAlign: 'right' }}
          onClick={getTextAlignHandler('right')}
          title={item?.title?.right}
          aria-label={item?.title?.right}
        >
          <FormatAlignRight width={18} height={18} />
        </ToggleButton>
        <ToggleButton
          editor={editor}
          action={{ textAlign: 'justify' }}
          onClick={getTextAlignHandler('justify')}
          title={item?.title?.justify}
          aria-label={item?.title?.justify}
        >
          <FormatAlignJustify width={18} height={18} />
        </ToggleButton>
      </>
    );
  };

  const renderYouTubeButton = (_item) => {
    return (
      <ToolbarBaseButton onClick={handleToggleYoutubeModal}>
        <YouTube width={18} height={18} />
      </ToolbarBaseButton>
    );
  };

  const renderIFrameButton = (_item) => {
    return (
      <ToolbarBaseButton onClick={handleToggleIFrameModal}>
        <WebAsset width={18} height={18} />
      </ToolbarBaseButton>
    );
  };

  const renderLinkButton = (_item) => {
    return (
      <ToolbarBaseButton onClick={handleToggleLinkModal}>
        {editor.isActive('link') ? (
          <LinkOff width={18} height={18} />
        ) : (
          <InsertLink width={18} height={18} />
        )}
      </ToolbarBaseButton>
    );
  };

  const renderFontSizeSelect = (_item) => {
    return (
      <ToolbarDropdown
        value={getCurrentFont()}
        onChange={handleSetFontSize}
        select
        fullWidth={false}
        sx={{
          width: '100px',
        }}
        slotProps={{
          select: {
            IconComponent: ArrowDropDownIcon,
            slotProps: {
              menu: {
                slotProps: {
                  paper: {
                    sx: {
                      boxShadow: 'rgba(99, 99, 99, 0.2) 0px 2px 8px 0px',
                    },
                  },
                },
              },
            },
          },
        }}
      >
        {FONT_SIZES.map((font) => (
          <ToolbarMenuItem key={font} value={font}>
            {font}
          </ToolbarMenuItem>
        ))}
      </ToolbarDropdown>
    );
  };

  const renderAdvanceTableButton = (_item) => {
    return (
      <Button
        onClick={handleInsertAdvanceTableFiled}
        title="Insert Advance Table"
        aria-label="Insert advance table node"
        variant="outlined"
        size="small"
      >
        Advance Table
      </Button>
    );
  };
  const renderAddChartButton = (_item) => {
    return (
      <Button
        onClick={handleAddChart}
        title="Add Chart"
        aria-label="Add chart"
        variant="outlined"
        size="small"
      >
        Add Chart
      </Button>
    );
  };

  const renderToolbarItem = (item) => {
    const renderFunction = {
      toggle: () => renderToggleButton(item),
      divider: renderDivider,
      undo: renderUndoButton,
      redo: renderRedoButton,
      heading: renderHeadingDropdown,
      image: renderImageButton,
      table: renderTableButton,
      youtube: renderYouTubeButton,
      iframe: renderIFrameButton,
      textAlign: renderTextAlignControls,
      link: renderLinkButton,
      fontSize: renderFontSizeSelect,
      AdvanceTableWithFields: renderAdvanceTableButton,
      addChart: renderAddChartButton,
    };

    if (Object.keys(renderFunction).includes(item.type)) {
      return renderFunction[item.type](item);
    }
    return null;
  };

  return (
    <SolyticsEditorToolbarRoot {...props}>
      {toolbarItems
        .filter((item) => !hiddenToolbarItems.includes(item?.type))
        ?.filter((item) => !hiddenToolbarItems.includes(item?.action))
        .map((item) => renderToolbarItem(item))}
      <ImageUploadModal
        open={uploadImageModalOpen}
        onClose={handleToggleUploadImageModal}
        onConfirm={addImage}
      />
      <TableBubbleMenu editor={editor} />
      <ResizableImageBubbleMenu editor={editor} />
      <IFrameModal
        mode="youtube"
        open={youtubeModalOpen}
        onClose={handleToggleYoutubeModal}
        onSubmit={handleInsertYoutubeVideo}
      />
      <IFrameModal
        mode="iframe"
        open={iframeModalOpen}
        onClose={handleToggleIFrameModal}
        onSubmit={handleInsertIframe}
      />
      <LinkModal
        open={linkModalOpen}
        onSubmit={handleInsertSetLink}
        onClose={handleToggleLinkModal}
        currentURL={currentLink}
      />
    </SolyticsEditorToolbarRoot>
  );
};
SolyticsEditorToolbar.propTypes = {
  editor: PropTypes.instanceOf(Editor).isRequired,
};

export default SolyticsEditorToolbar;
